'use client';

import { useMemo } from 'react';
import { ExternalLink, Phone, Download } from 'lucide-react';
import type { TenantRow } from '../types';

type VoiceRow = {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  host: string;
  did: string;
  provider: string;
  is_active: boolean;
};

export function VoicePanel({ tenants, loading }: { tenants: TenantRow[]; loading: boolean }) {
  const rows = useMemo<VoiceRow[]>(() => {
    const out: VoiceRow[] = [];
    for (const t of tenants) {
      const host =
        t.domains.find((d) => d.is_primary && d.is_active)?.host ??
        t.domains.find((d) => d.is_active)?.host ??
        `${t.slug}.okai.pt`;
      for (const v of t.voice_numbers) {
        out.push({
          tenantId: t.id,
          tenantName: t.name,
          tenantSlug: t.slug,
          host,
          did: v.did_number,
          provider: v.provider,
          is_active: v.is_active,
        });
      }
    }
    return out.sort((a, b) => a.tenantName.localeCompare(b.tenantName));
  }, [tenants]);

  const activeCount = rows.filter((r) => r.is_active).length;

  if (loading) {
    return <p className="text-sm text-ink-500">A carregar numeros de voz...</p>;
  }

  return (
    <div className="space-y-4">
      <div className="sa-toolbar">
        <span className="text-sm text-ink-500">Inventario de DIDs</span>
        <a href="/api/super-admin/export?type=voice" className="btn btn-secondary btn-sm">
          <Download className="w-4 h-4" /> Exportar CSV
        </a>
      </div>
      <div className="sa-panel-grid sa-panel-grid-3">
        <div className="sa-kpi">
          <p className="sa-kpi-label">DIDs configurados</p>
          <p className="sa-kpi-value">{rows.length}</p>
        </div>
        <div className="sa-kpi">
          <p className="sa-kpi-label">Ativos</p>
          <p className="sa-kpi-value">{activeCount}</p>
        </div>
        <div className="sa-kpi">
          <p className="sa-kpi-label">Tenants com voz</p>
          <p className="sa-kpi-value">{new Set(rows.map((r) => r.tenantId)).size}</p>
        </div>
      </div>

      <section className="sa-card">
        <header className="sa-card-header">
          <h3 className="sa-card-title flex items-center gap-2">
            <Phone className="w-4 h-4" /> Inventario de numeros
          </h3>
          <p className="sa-card-desc">Todos os DIDs associados a tenants na plataforma</p>
        </header>

        {rows.length === 0 ? (
          <p className="text-sm text-ink-500">Nenhum numero de voz configurado.</p>
        ) : (
          <div className="sa-table-wrap">
            <table className="sa-table">
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th>DID</th>
                  <th>Provider</th>
                  <th>Estado</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.tenantId}-${r.did}`}>
                    <td>
                      <div className="font-medium">{r.tenantName}</div>
                      <div className="text-xs font-mono text-ink-500">{r.tenantSlug}</div>
                    </td>
                    <td className="font-mono">+{r.did}</td>
                    <td>{r.provider || '—'}</td>
                    <td>
                      <span className={`sa-status sa-status-${r.is_active ? 'ok' : 'muted'}`}>
                        {r.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td>
                      <a href={`https://${r.host}/admin/chamadas`} target="_blank" rel="noreferrer" className="sa-chip">
                        <ExternalLink className="w-3.5 h-3.5" /> Chamadas
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
